import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAIFormCheckVisibility } from '../hooks/useAIFormCheckVisibility';
import { ArrowRight, ChevronDown, User as UserIcon, LogIn, UserPlus, CalendarClock, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Signed-in user chip that opens a "My Account" dropdown from any public header.
 *
 * IMPORTANT: this menu is the ONLY way a student ever ends up on their personal
 * pages (bookings, credits, medical info, etc.). The site itself never
 * auto-routes them there after login. Every item is opt-in.
 *
 * Menu items match the spec exactly:
 *   • My Profile           → /profile
 *   • My Bookings          → /my-bookings
 *   • My Packages & Credits → /buy-packages
 *   • Payment History      → /my-payments
 *   • Membership Status    → /membership
 *   • Medical Information  → /profile?section=medical
 *   • Log Out (action)
 */
export const AccountMenu = ({ user, onLogout, onNavigate }) => {
  const { visible: aiVisible } = useAIFormCheckVisibility();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const [installments, setInstallments] = useState({ count: 0, overdue: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/me/installments/active-reminders`, {
          params: { days_ahead: 5 }, withCredentials: true,
        });
        const items = r.data || [];
        if (!cancelled) setInstallments({ count: items.length, overdue: items.some((x) => x.is_overdue) });
      } catch (_e) { /* silent — never block header render */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const initial = (user.name || 'U')
    .replace(/^Coach\s+/i, '')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const tierLabel = user.is_family_member
    ? 'Family'
    : (({ platinum: 'Platinum', gold: 'Gold', lgra_active: 'LGRA Active', silver: 'LGRA Active', bronze: 'LGRA Active', general: 'Guest' })[
        String(user.membership_tier || '').toLowerCase()
      ] || 'Member');

  const go = (path) => {
    setOpen(false);
    onNavigate(path);
  };

  // Priority items (per Feb 14 2026 spec) surface first: My Bookings,
  // My Packages & Credits, LGRA Wallet, My Profile. Secondary items follow
  // below a subtle divider so nothing hard-to-reach gets buried.
  const items = [
    { label: 'My Bookings',             path: '/my-bookings',                testId: 'menu-bookings' },
    { label: 'My Classes & Recordings', path: '/my-classes-recordings',      testId: 'menu-classes-recordings' },
    { label: 'My Races',                path: '/my-races',                   testId: 'menu-my-races' },
    { label: 'My Packages & Credits',   path: '/buy-packages',               testId: 'menu-credits' },
    { label: 'LGRA Wallet',             path: '/my-wallet',                  testId: 'menu-wallet' },
    { label: 'My Profile',              path: '/profile',                    testId: 'menu-profile' },
    { divider: true },
    { label: 'My Membership',           path: '/my-membership',              testId: 'menu-membership' },
    { label: 'Family & Participants',   path: '/family',                     testId: 'menu-family' },
    { label: 'Payment History',         path: '/my-payments',                testId: 'menu-payments' },
    { label: 'My Reservations',         path: '/my-reservations',            testId: 'menu-reservations' },
    { label: 'My AI Form Checks',       path: '/my-ai-form-checks',          testId: 'menu-ai-form-checks' },
    { label: 'My Technique Assessments', path: '/my-technique-assessments',  testId: 'menu-technique-assessments' },
    { label: 'My Promotions',           path: '/my-promotions',              testId: 'menu-my-promotions' },
    { label: 'AI Image Studio',         path: '/ai-image-studio',            testId: 'menu-ai-image-studio' },
    { label: 'Medical Information',     path: '/profile?section=medical',    testId: 'menu-medical' },
    { label: 'Booking Help',            path: '/booking-help',               testId: 'menu-booking-help' },
  ];

  // A linked family member can't buy packages, see billing/wallet, manage
  // membership, or manage other family members — hide those entries.
  const memberHiddenPaths = new Set(['/buy-packages', '/my-wallet', '/my-payments', '/family', '/my-membership']);
  const visibleItems = (user.is_family_member
    ? items.filter((it) => !it.divider && !memberHiddenPaths.has(it.path))
    : items).filter((it) => aiVisible || it.path !== '/my-ai-form-checks');

  return (
    <div ref={ref} className="relative" data-testid="account-menu">
      <button
        onClick={() => setOpen(!open)}
        aria-label="Open My Account menu"
        className="flex items-center gap-2 pl-1 pr-2 h-10 rounded-full bg-white border-2 border-[#F3E8FF] hover:border-[#A855F7] hover:shadow transition-all"
        data-testid="account-menu-button"
      >
        <div className="relative">
          <div
            className="w-8 h-8 rounded-full bg-gradient-to-br from-[#A855F7] to-[#7E22CE] text-white flex items-center justify-center text-xs font-black"
            data-testid="account-menu-avatar"
          >
            {initial}
          </div>
          {installments.count > 0 && (
            <span
              className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${installments.overdue ? 'bg-red-600' : 'bg-[#A855F7]'}`}
              data-testid="account-installment-dot"
              aria-hidden
            />
          )}
        </div>
        <span className="hidden sm:inline text-[11px] font-black tracking-tight text-[#111827] max-w-[110px] truncate">
          {(user.name || '').split(' ')[0] || 'My Account'}
        </span>
        <ChevronDown size={14} className={`hidden sm:block text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-64 rounded-xl bg-white border border-[#E5E7EB] shadow-2xl overflow-hidden z-50"
          data-testid="account-menu-dropdown"
        >
          <div className="px-4 py-3 bg-gradient-to-br from-[#F3E8FF] to-white border-b">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#A855F7] mb-0.5">{tierLabel} Member</div>
            <div className="text-sm font-black tracking-tight leading-tight truncate">{user.name || 'My Account'}</div>
            <div className="text-[11px] text-muted-foreground truncate">{user.email}</div>
          </div>
          {installments.count > 0 && (
            <button
              onClick={() => go('/my-payments')}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 border-b transition-colors ${installments.overdue ? 'bg-red-50 hover:bg-red-100 text-red-700' : 'bg-[#F7F2FF] hover:bg-[#EFE7FF] text-[#5530B5]'}`}
              data-testid="menu-installment-alert"
            >
              {installments.overdue ? <AlertTriangle size={15} /> : <CalendarClock size={15} />}
              <span className="font-black flex-1">
                {installments.overdue
                  ? `${installments.count} installment${installments.count === 1 ? '' : 's'} overdue`
                  : `${installments.count} payment${installments.count === 1 ? '' : 's'} due soon`}
              </span>
              <ArrowRight size={12} />
            </button>
          )}
          <div className="py-1">
            {visibleItems.map((it, idx) => (
              it.divider ? (
                <div key={`div-${idx}`} className="my-1 border-t border-[#E9E2F3]" aria-hidden />
              ) : (
                <button
                  key={it.testId}
                  onClick={() => go(it.path)}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F8FAFC] transition-colors flex items-center justify-between"
                  data-testid={it.testId}
                >
                  <span>{it.label}</span>
                  <ArrowRight size={12} className="text-muted-foreground" />
                </button>
              )
            ))}
          </div>
          <div className="border-t">
            <button
              onClick={() => { setOpen(false); onLogout(); onNavigate('/'); }}
              className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors font-medium"
              data-testid="menu-logout"
            >
              Log Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;

/**
 * GuestAccountMenu — the signed-out counterpart to <AccountMenu>. Replaces the
 * old separate "Sign In" + "Join Now" header buttons with ONE "Account" chip
 * (matching the signed-in avatar chip) whose dropdown offers Sign In and
 * Join LGRA / Create Account.
 */
export const GuestAccountMenu = ({ onSignIn, onJoin }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const choose = (fn) => { setOpen(false); fn && fn(); };

  return (
    <div ref={ref} className="relative" data-testid="guest-account-menu">
      <button
        onClick={() => setOpen(!open)}
        aria-label="Open account menu"
        className="flex items-center gap-1.5 h-9 sm:h-10 pl-1 pr-2 sm:pr-3 rounded-full bg-white border-2 border-[#F3E8FF] hover:border-[#A855F7] hover:shadow transition-all"
        data-testid="guest-account-menu-button"
      >
        <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-[#A855F7] to-[#7E22CE] text-white flex items-center justify-center">
          <UserIcon size={14} />
        </span>
        <span className="hidden sm:inline text-[11px] font-black tracking-tight text-[#111827]">Account</span>
        <ChevronDown size={14} className={`text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-60 rounded-xl bg-white border border-[#E5E7EB] shadow-2xl overflow-hidden z-50"
          data-testid="guest-account-menu-dropdown"
        >
          <div className="px-4 py-3 bg-gradient-to-br from-[#F3E8FF] to-white border-b">
            <div className="text-[10px] font-black uppercase tracking-widest text-[#A855F7] mb-0.5">Welcome to LGRA</div>
            <div className="text-[11px] text-muted-foreground">Sign in or create your account</div>
          </div>
          <div className="py-1">
            <button
              onClick={() => choose(onSignIn)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F8FAFC] transition-colors flex items-center gap-2.5"
              data-testid="guest-menu-signin"
            >
              <LogIn size={15} className="text-[#7E22CE]" /> <span className="font-semibold">Sign In</span>
            </button>
            <button
              onClick={() => choose(onJoin)}
              className="w-full text-left px-4 py-2.5 text-sm hover:bg-[#F8FAFC] transition-colors flex items-center gap-2.5"
              data-testid="guest-menu-join"
            >
              <UserPlus size={15} className="text-[#7E22CE]" /> <span className="font-semibold">Join LGRA / Create Account</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
